import { z } from "zod";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

const Client = z.object({
  id: z.number(),
  name: z.string(),
});

class UsersEndpoint {
  public static readonly basePath = "users";
  public static readonly schemas = {
    login: {
      body: z.object({
        username: z.string(),
        password: z.string(),
      }),
      response: z
        .object({
          success: z.literal(false),
          message: z.string().nullish(),
        })
        .or(
          z.object({
            success: z.literal(true),
            token: z.string().nullish(),
          }),
        ),
    },
    profile: {
      response: z.object({
        username: z.string(),
        is_superuser: z.boolean(),
        first_name: z.string().nullable().optional(),
        last_name: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        last_login: z.string().datetime().nullable().optional(),
        date_joined: z.string().datetime().nullable().optional(),
        member_since: z.string().datetime().nullable().optional(),
        is_staff: z.boolean().optional(),
        client: Client.nullable().optional(),
        /** Every client the account belongs to, the active one included. */
        clients: Client.array().default([]),
        can_switch_client: z.boolean().default(false),
        auth_method: z.string().optional(),
        auth_provider: z.string().nullable().optional(),
        has_password: z.boolean().optional(),
        auth_providers: z.string().array().optional(),
        user_permissions: z
          .object({
            name: z.string(),
            app_label: z.string(),
            codename: z.string(),
          })
          .array()
          .nullable()
          .optional(),
      }),
    },
    switchClient: {
      body: z.object({
        client_id: z.number(),
      }),
    },
    updateProfile: {
      body: z.object({
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        email: z.string().nullable(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public async login(
    body: z.infer<typeof UsersEndpoint.schemas.login.body>,
  ): ActionReturnType<z.infer<typeof UsersEndpoint.schemas.login.response>> {
    const bodyValidated = validateInput(UsersEndpoint.schemas.login.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Users", "login"],
      method: "post",
      path: [UsersEndpoint.basePath, "login"],
      json: bodyValidated.data,
      response: UsersEndpoint.schemas.login.response,
      skipAuth: true,
    });
  }

  public async logout(): ActionReturnType<undefined> {
    const result = await request({
      log: ["Users", "logout"],
      method: "post",
      path: [UsersEndpoint.basePath, "logout"],
    });
    if (!result.ok) return result;
    this.api.removeAuthToken();
    return result;
  }

  public profile(): ActionReturnType<z.infer<typeof UsersEndpoint.schemas.profile.response>> {
    return request({
      log: ["Users", "profile"],
      method: "get",
      path: [UsersEndpoint.basePath, "profile"],
      response: UsersEndpoint.schemas.profile.response,
    });
  }

  /** Moves the whole account — every tab and session — into another of its clients. */
  public switchClient(clientId: number): ActionReturnType<z.infer<typeof UsersEndpoint.schemas.profile.response>> {
    return request({
      log: ["Users", "switchClient"],
      method: "post",
      path: [UsersEndpoint.basePath, "me", "client"],
      json: { client_id: clientId } satisfies z.input<typeof UsersEndpoint.schemas.switchClient.body>,
      response: UsersEndpoint.schemas.profile.response,
    });
  }

  public async updateProfile(body: z.infer<typeof UsersEndpoint.schemas.updateProfile.body>): ActionReturnType<void> {
    const bodyValidated = validateInput(UsersEndpoint.schemas.updateProfile.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Users", "updateProfile"],
      method: "post",
      path: [UsersEndpoint.basePath, "update-profile"],
      json: bodyValidated.data,
    });
  }
}

export { UsersEndpoint };
